import React, { useContext, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Button,
  color,
  fontSize,
  fontWeight,
  space,
  Input,
  Alert,
  device,
} from '@ticketswap/solar'
import styled from '@emotion/styled'
import gql from 'graphql-tag'
import { Mutation } from '@apollo/react-components'
import { Formik } from 'formik'
import * as yup from 'yup'
import FacebookAuthButton from './FacebookAuthButton'
import {
  AUTHENTICATION_STARTED,
  AUTHENTICATION_COMPLETED,
} from '~/lib/tracking-events'
import { createMarkup, track } from '~/utils'
import { ViewerContext } from '../ViewerContext'
import AppleIDButton from './AppleIDButton'

export const SEND_LOGIN_LINK = gql`
  mutation sendLoginLink($input: SendLoginLinkInput!) {
    sendLoginLink(input: $input) {
      errors {
        key
        message
        code
      }
    }
  }
`

const Divider = styled.p`
  color: ${color.space};
  font-size: ${fontSize[18]};
  font-weight: ${fontWeight.semiBold};
  display: flex;
  flex-basis: 100%;
  align-items: center;
  margin: ${space[16]} 0;
  justify-content: center;

  &::after,
  &::before {
    content: '';
    flex-grow: 1;
    background: ${color.spaceLightest};
    height: 1px;
    font-size: 0;
    line-height: 0;
  }

  &:before {
    margin: 0 ${space[16]} 0 0;
  }

  &:after {
    margin: 0 0 0 ${space[16]};
  }
`

const Form = styled.form`
  display: grid;
  grid-gap: ${space[8]};
`

const TermsAndConditions = styled.p`
  color: ${color.spaceLight};
  margin: 0;
  font-size: ${fontSize[16]};
  text-align: center;

  a {
    color: ${color.space};
  }
`

const StyledParagraph = styled.p`
  margin-bottom: ${space[16]};

  @media ${device.mobileL} {
    margin-bottom: ${space[24]};
  }
`

const SocialButtons = styled.div`
  display: grid;
  grid-gap: ${space[8]};
`

function Login({ t, ...props }) {
  const [errorMessage, setErrorMessage] = useState(null)
  const { setEmail } = useContext(ViewerContext)

  async function handleSubmit(values, send, setEmail) {
    track(AUTHENTICATION_STARTED, {
      method: 'email',
    })

    const {
      data: { sendLoginLink },
    } = await send({
      variables: {
        input: {
          ...values,
          redirectTo: props.redirectUrl || window.location.href,
        },
      },
    })

    if (
      sendLoginLink.errors.find(error => error.code === 'EMAIL_DOES_NOT_EXIST')
    ) {
      await setEmail(values.email)

      return props.onEmailSignup()
    }

    if (sendLoginLink.errors.length) {
      track(AUTHENTICATION_COMPLETED, {
        method: 'email',
        isNew: false,
        error: sendLoginLink.errors[0].code,
      })

      return setErrorMessage(sendLoginLink.errors[0].message)
    }

    track(AUTHENTICATION_COMPLETED, {
      method: 'email',
      isNew: false,
    })

    await setEmail(values.email)
    return props.onEmailLogin()
  }

  return (
    <>
      {props.text && (
        <StyledParagraph
          dangerouslySetInnerHTML={createMarkup(props.text, {
            renderInline: true,
          })}
        />
      )}

      <SocialButtons>
        <FacebookAuthButton
          t={t}
          onLogin={props.onSocialLogin}
          onSignup={props.onSocialSignup}
          option="login"
        >
          {t('auth.login.buttons.facebook')}
        </FacebookAuthButton>

        <AppleIDButton
          t={t}
          onLogin={props.onSocialLogin}
          onSignup={props.onSocialSignup}
        >
          {t('auth.login.buttons.apple')}
        </AppleIDButton>
      </SocialButtons>

      <Divider>{t('auth.login.divider.text')}</Divider>

      <Mutation mutation={SEND_LOGIN_LINK}>
        {(send, { loading }) => (
          <Formik
            initialValues={{ email: '' }}
            validationSchema={yup.object().shape({
              email: yup.string().email().required(t('generic.required')),
            })}
            onSubmit={values => handleSubmit(values, send, setEmail)}
          >
            {({
              values,
              isValid,
              dirty,
              handleChange,
              handleSubmit,
              handleReset,
            }) => (
              <Form onSubmit={handleSubmit}>
                {errorMessage && <Alert variant="error">{errorMessage}</Alert>}
                <Input
                  type="email"
                  name="email"
                  id="email"
                  label={t('auth.signupEmail.fields.email.label')}
                  hideLabel
                  value={values.email}
                  onChange={handleChange}
                  onReset={handleReset}
                  placeholder={t('auth.login.inputs.email')}
                />
                <Button
                  variant="success"
                  type="submit"
                  width="full"
                  loading={loading}
                  disabled={!isValid || !dirty}
                >
                  {t('auth.login.buttons.email')}
                </Button>
                <TermsAndConditions
                  dangerouslySetInnerHTML={createMarkup(
                    t('auth.login.info.terms'),
                    {
                      renderInline: true,
                      openLinksInNewTab: true,
                    }
                  )}
                />
              </Form>
            )}
          </Formik>
        )}
      </Mutation>
    </>
  )
}

Login.propTypes = {
  t: PropTypes.func.isRequired,
  redirectUrl: PropTypes.string,
  onEmailLogin: PropTypes.func.isRequired,
  onEmailSignup: PropTypes.func.isRequired,
  onSocialLogin: PropTypes.func.isRequired,
  onSocialSignup: PropTypes.func.isRequired,
  text: PropTypes.string,
}

export default Login
